import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Stack,
  Grid2,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  SimSerialNoMappingInfo,
  SimSerialNoMappingInsertForm,
  SimSerialNoMappingSearchForm,
  SimSerialNoMappingUpdateForm,
  Status,
} from "IF/interfaceType";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { useSessionStorage } from "IF/userSession";
import SimSerialNoMappingInputForm from "./SimSerialNoMappingInputForm";
import { convertNumberToFullWidthDigits, createTitle } from "Utils/format";
import ConfirmationModal from "./ConfirmationModal";
import { MessageModal } from "./MessageModal";
import { DataContext } from "Contexts/DataProvider";
import { useThemeMode } from "Contexts/ThemeContext";

export const SimSerialNoMapping = () => {
  const { getUserData } = useSessionStorage();
  const userId: string = getUserData()?.id;

  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { setChoiceMenu, toggleMenu } = sidebarContext;

  const initUpdateData: SimSerialNoMappingUpdateForm = {
    simId: "",
    serialNo: "",
    oldSimId: "",
    oldSerialNo: "",
    updateUser: userId,
  };

  const initDataSearch: SimSerialNoMappingSearchForm = {
    simId: "",
    serialNo: "",
    companyCode: "",
    companyName: "",
    status: "",
  };

  const initDataInsert: SimSerialNoMappingInsertForm = {
    index: 0,
    simId: "",
    serialNo: "",
    result: "OK",
    createUser: userId,
    updateUser: userId,
  };

  const [data, setData] = useState<SimSerialNoMappingInfo[]>([]);

  const [initEditFormData, setInitEditFormData] = useState<
    SimSerialNoMappingInfo[]
  >([]);

  const [searchForm, setSearchForm] =
    useState<SimSerialNoMappingSearchForm>(initDataSearch);

  const [inputData, setInputData] = useState<SimSerialNoMappingInsertForm[]>(
    Array.from({ length: 50 }, (_, idx) => ({ ...initDataInsert, index: idx }))
  );
  const [editFormData, setEditFormData] =
    useState<SimSerialNoMappingUpdateForm>(initUpdateData);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const theme = useTheme();
  const { mode } = useThemeMode();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [inputTableBodyHeight, setInputTableBodyHeight] = useState(600);
  const [xsWidth, setXsWidth] = useState(0);

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: string,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  const title = "シリアルNo紐づけ設定";

  useEffect(() => {
    document.title = createTitle(title);
    setChoiceMenu(title);
    toggleMenu("YKC専用");
  }, [setChoiceMenu, toggleMenu]);

  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 210;
      const windowWidth = window.innerWidth - 52;
      setInputTableBodyHeight(windowHeight);
      setTableBodyHeight(windowHeight - 195);
      setXsWidth(windowWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    getInitData();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 初期検索
  const getInitData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/SimSerialNoMapping/GetSimSerialNoMapping`,
        {}
      );
      setData(response.data);
      setInitEditFormData(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("紐づけ済一覧の検索処理に失敗しました");
      console.error("紐づけ済一覧の処理に失敗しました:", error);
      setData([]);
      setInitEditFormData([]);
    }
  };

  const handleSearch = () => {
    getData();
  };

  // 条件あり検索
  const getData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/SimSerialNoMapping/GetSimSerialNoMapping`,
        {
          params: searchForm,
        }
      );
      setData(response.data);
      setInitEditFormData(response.data);
      handleInitUpdate();
    } catch (error) {
      setModalOpen(true);
      setMessage("紐づけ済一覧の検索処理に失敗しました");
      console.error("紐づけ済一覧の処理に失敗しました:", error);
      setData([]);
      setInitEditFormData([]);
    }
  };

  const searchHandleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    let { name, value } = e.target;
    // companyCode の場合は大文字変換
    if (name === "companyCode") {
      value = value.toUpperCase();
    }
    setSearchForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setSearchForm(initDataSearch);
  };

  const handleInput = async () => {
    const validInputs = inputData.filter(
      (item) => !isNullOrEmpty(item.simId) || !isNullOrEmpty(item.serialNo)
    );

    if (validInputs.length === 0) {
      setModalOpen(true);
      setMessage("登録するデータがありません。");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/SimSerialNoMapping/PostSimSerialNoMapping`,
        validInputs
      );

      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("紐づけ情報が登録されました。");
        setInputData(
          Array.from({ length: 50 }, (_, idx) => ({
            ...initDataInsert,
            index: idx,
          }))
        );
        handleSearch();
        if (isXs) {
          setIsFormDialogOpen(false);
        }
      }
    } catch (error: any) {
      setModalOpen(true);
      if (
        error.response &&
        error.response.status === RESPONSE_CODE.BAD_REQUEST
      ) {
        if (error.response.data && error.response.data.resultList) {
          setMessage("エラーがありました。エラー行を確認してください。");

          const updatedItems = error.response.data.resultList;
          setInputData((prevData) =>
            prevData.map((row) => {
              const found = updatedItems.find(
                (u: any) => u.index === row.index
              );
              return found ? { ...row, result: found.result } : row;
            })
          );
        } else if (error.response.data) {
          setMessage(error.response.data);
        } else {
          setMessage("紐づけ情報の登録中にクライアントエラーが発生しました。");
        }
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("紐づけ情報の登録に失敗しました。");
        console.error("紐づけ情報の登録に失敗しました。", error);
      }
    }
  };

  const getModifiedElements = () => {
    if (
      editIndex !== null &&
      editIndex >= 0 &&
      editIndex < initEditFormData.length &&
      (editFormData.simId !== initEditFormData[editIndex]?.simId ||
        editFormData.serialNo !== initEditFormData[editIndex]?.serialNo)
    ) {
      return editFormData;
    } else {
      return {};
    }
  };

  const handleUpdate = async () => {
    let modifiedElements = getModifiedElements();
    if (Object.keys(modifiedElements).length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    try {
      const response = await axios.put(
        `${API_BASE_URL}/SimSerialNoMapping/PutSimSerialNoMapping`,
        { ...editFormData, updateUser: userId }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        getData();
        setModalOpen(true);
        setMessage("紐づけ情報を更新しました。");
        handleInitUpdate();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (
        error.response &&
        error.response.status === RESPONSE_CODE.BAD_REQUEST
      ) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("紐づけ情報の更新に失敗しました。");
        console.error("紐づけ情報の更新に失敗しました。", error);
      }
    }
  };

  const handleDelete = async (index: number) => {
    if (index < 0 || index >= data.length) {
      console.error("無効な削除対象インデックス:", index);
      return;
    }
    const deleteData = data[index];
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/SimSerialNoMapping/DeleteSimSerialNoMapping`,
        {
          headers: { "Content-Type": "application/json" },
          data: deleteData,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("紐づけ情報が削除されました。");
        getData();
      }
    } catch (error) {
      setModalOpen(true);
      setMessage("紐づけ情報の削除に失敗しました。");
      console.error("紐づけ情報の削除に失敗しました:", error);
    }
  };

  const handleEdit = (index: number) => {
    if (editIndex !== null && editIndex !== index) {
      handleCancel();
    }

    setEditIndex(index);
    setEditFormData({
      simId: data[index].simId,
      serialNo: data[index].serialNo,
      oldSimId: data[index].simId,
      oldSerialNo: data[index].serialNo,
      updateUser: userId,
      updateDatetime: data[index].updateDatetime,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  const handleInputChange = (
    index: number,
    field: "simId" | "serialNo",
    value: string
  ) => {
    setInputData((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], [field]: value };
      return newData;
    });
  };

  const handleCancel = () => {
    handleInitUpdate();
  };

  const handleInitUpdate = () => {
    setEditIndex(null);
    setEditFormData(initUpdateData);
  };

  function isNullOrEmpty(value: string | null | undefined): boolean {
    return value == null || value.trim().length === 0;
  }

  const inputColumnWidths: Record<string, string> = {
    rowNo: "5%",
    simId: "45%",
    serialNo: "50%",
  };

  const columnWidths: Record<string, string> = {
    simId: "25%",
    serialNo: "15%",
    companyName: "30%",
    companyCode: "15%",
    action: "15%",
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />

      {isXs && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => setIsFormDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          IMSI・シリアルNo登録
        </Button>
      )}

      <Dialog
        open={isFormDialogOpen}
        onClose={() => setIsFormDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>IMSI・シリアルNo 登録</DialogTitle>
        <DialogContent dividers>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: inputTableBodyHeight,
              overflowY: "auto",
              backgroundColor: theme.palette.custom.row,
            }}
          >
            <Table
              sx={{
                "& th, & td": {
                  textAlign: "center",
                  verticalAlign: "middle",
                },
                "& th": {
                  color: "#fff",
                },
              }}
            >
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: theme.palette.custom.tableHeader,
                  zIndex: 1,
                }}
              >
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      padding: "2px 4px",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      verticalAlign: "middle",
                    }}
                  >
                    <Typography
                      id="linked-simid-list-header"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color: "#fff",
                        marginBottom: 0,
                        backgroundColor: theme.palette.custom.tableHeader,
                        textAlign: "center",
                      }}
                    >
                      IMSI・シリアルNo入力欄
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow aria-labelledby="linked-simid-list-header">
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      verticalAlign: "middle",
                      width: inputColumnWidths.rowNo,
                    }}
                  ></TableCell>
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      verticalAlign: "middle",
                      width: inputColumnWidths.simId,
                    }}
                  >
                    IMSI
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "2px 4px",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      verticalAlign: "middle",
                      width: inputColumnWidths.serialNo,
                    }}
                  >
                    シリアルNo
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inputData.map((row) => (
                  <SimSerialNoMappingInputForm
                    key={row.index}
                    index={row.index}
                    simId={row.simId}
                    serialNo={row.serialNo}
                    result={row.result}
                    handleChange={handleInputChange}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormDialogOpen(false)} color="secondary">
            キャンセル
          </Button>
          <Button onClick={handleInput} variant="contained" color="primary">
            登録
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={(theme) => ({
          width: "100%",
          margin: "0 auto",
          [theme.breakpoints.only("xs")]: {
            maxWidth: xsWidth ? `${xsWidth}px` : "100%",
          },
        })}
      >
        <Grid2 container spacing={2}>
          <Grid2
            size={{ xs: 12, sm: 4 }}
            sx={{
              display: { xs: "none", sm: "block" },
              padding: 2,
              backgroundColor: "background.default",
              borderRadius: 2,
              boxShadow: 3,
              gap: 1,
            }}
          >
            <Grid2 container direction="column" spacing={1}>
              <Grid2>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: inputTableBodyHeight,
                    overflowY: "auto",
                    backgroundColor: theme.palette.custom.row,
                  }}
                >
                  <Table
                    sx={{
                      "& th, & td": {
                        textAlign: "center",
                        verticalAlign: "middle",
                      },
                      "& th": {
                        color: "#fff",
                      },
                    }}
                  >
                    <TableHead
                      sx={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: theme.palette.custom.tableHeader,
                        zIndex: 1,
                      }}
                    >
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          sx={{
                            padding: "2px 4px",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            verticalAlign: "middle",
                          }}
                        >
                          <Typography
                            id="linked-simid-list-header"
                            gutterBottom
                            sx={{
                              fontWeight: "bold",
                              color: "#fff",
                              marginBottom: 0,
                              backgroundColor: theme.palette.custom.tableHeader,
                              textAlign: "center",
                            }}
                          >
                            IMSI・シリアルNo入力欄
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow aria-labelledby="linked-simid-list-header">
                        <TableCell
                          sx={{
                            padding: "2px 4px",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            verticalAlign: "middle",
                            width: inputColumnWidths.rowNo,
                          }}
                        ></TableCell>
                        <TableCell
                          sx={{
                            padding: "2px 4px",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            verticalAlign: "middle",
                            width: inputColumnWidths.simId,
                          }}
                        >
                          IMSI
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "2px 4px",
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            verticalAlign: "middle",
                            width: inputColumnWidths.serialNo,
                          }}
                        >
                          シリアルNo
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inputData.map((row) => (
                        <SimSerialNoMappingInputForm
                          key={row.index}
                          index={row.index}
                          simId={row.simId}
                          serialNo={row.serialNo}
                          result={row.result}
                          handleChange={handleInputChange}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid2>
              <Grid2 container justifyContent="flex-end" sx={{ pt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleInput}
                >
                  登録
                </Button>
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2
            size={{ xs: 12, sm: 8 }}
            sx={{
              padding: 2,
              backgroundColor: "background.default",
              borderRadius: 2,
              boxShadow: 3,
              gap: 1,
            }}
          >
            <Box sx={{ marginBottom: 2 }}>
              <Paper
                sx={{
                  padding: "10px 10px",
                  backgroundColor: theme.palette.custom.sForm,
                  border: `3px solid ${theme.palette.custom.paperBorderColor}`,
                }}
              >
                <Box sx={{ width: "100%", marginBottom: 1, marginTop: 0 }}>
                  <Typography fontWeight="fontWeightBold">
                    IMSI・シリアルNo検索
                  </Typography>
                </Box>
                <Grid2 container spacing={1} alignItems="center">
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="IMSI"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={searchForm.simId}
                      name="simId"
                      onChange={searchHandleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="シリアルNo"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={searchForm.serialNo}
                      name="serialNo"
                      onChange={searchHandleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="企業コード"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={searchForm.companyCode}
                      name="companyCode"
                      onChange={searchHandleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="企業名"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={searchForm.companyName}
                      name="companyName"
                      onChange={searchHandleChange}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>ステータス</InputLabel>
                      <Select
                        size="small"
                        label="ステータス"
                        value={searchForm.status}
                        name="status"
                        onChange={searchHandleChange}
                      >
                        <MenuItem value="">
                          <em>　</em>
                        </MenuItem>
                        <MenuItem value={Status.Inactive}>利用なし</MenuItem>
                        <MenuItem value={Status.Active}>利用中</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{ xs: 4, sm: 4, md: 6 }}></Grid2>
                  <Grid2
                    size={{ xs: 8, sm: 8, md: 6 }}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleSearch}
                      fullWidth
                    >
                      検索
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleReset}
                      fullWidth
                    >
                      クリア
                    </Button>
                  </Grid2>
                </Grid2>
              </Paper>
            </Box>

            <Box sx={{ paddingBottom: 1 }}>
              <Typography
                fontWeight="fontWeightBold"
                sx={{
                  display: "inline-block",
                  borderBottom: "5px solid #FF8C00",
                  paddingBottom: "0.5px",
                }}
              >
                検索結果：{convertNumberToFullWidthDigits(data.length)}件
              </Typography>
            </Box>

            {isXs ? (
              <Stack spacing={1}>
                {data.length === 0 ? (
                  <Typography variant="body2" textAlign="center" sx={{ py: 2 }}>
                    検索結果はありません。
                  </Typography>
                ) : (
                  data.map((row, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{ backgroundColor: theme.palette.custom.row, p: 2 }}
                    >
                      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                        {editIndex === index ? (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body1" fontWeight="bold">
                              IMSI
                            </Typography>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="none"
                              value={editFormData.simId ?? ""}
                              name="simId"
                              onChange={handleChange}
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="body1" fontWeight="bold">
                              シリアルNo
                            </Typography>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="none"
                              value={editFormData.serialNo ?? ""}
                              name="serialNo"
                              onChange={handleChange}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body1" fontWeight="bold">
                              IMSI
                            </Typography>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="none"
                              value={row.simId ?? ""}
                              disabled
                              name="simId"
                              onChange={handleChange}
                              sx={{ mb: 1 }}
                              InputProps={{
                                sx: {
                                  // height: 30,
                                  // fontSize: "0.8rem",
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor:
                                      mode === "dark" ? "white" : "black",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                },
                              }}
                            />
                            <Typography variant="body1" fontWeight="bold">
                              シリアルNo
                            </Typography>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="none"
                              value={row.serialNo ?? ""}
                              disabled
                              name="serialNo"
                              onChange={handleChange}
                              InputProps={{
                                sx: {
                                  // height: 30,
                                  // fontSize: "0.8rem",
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor:
                                      mode === "dark" ? "white" : "black",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                },
                              }}
                            />
                          </Box>
                        )}
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body1" fontWeight="bold">
                            利用企業コード
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 0.5 }}>
                            {row.companyCode || "　"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body1" fontWeight="bold">
                            利用企業名
                          </Typography>
                          <Typography variant="body1">
                            {row.companyName || "　"}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions sx={{ p: 0, justifyContent: "flex-end" }}>
                        {editIndex === index ? (
                          <>
                            <Tooltip title={"保存"} placement="top" arrow>
                              <IconButton size="small" onClick={handleUpdate}>
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"キャンセル"} placement="top" arrow>
                              <IconButton size="small" onClick={handleCancel}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title={"編集"} placement="top" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(index)}
                                  disabled={
                                    !isNullOrEmpty(row.companyCode) &&
                                    !isNullOrEmpty(row.companyName)
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={"削除"} placement="top" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleConfirmModalOpen(
                                      "選択したデータを削除します。よろしいですか？",
                                      () => handleDelete(index)
                                    )
                                  }
                                  disabled={
                                    !isNullOrEmpty(row.companyCode) &&
                                    !isNullOrEmpty(row.companyName)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </CardActions>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
              // </Box>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: tableBodyHeight,
                  overflowY: "auto",
                  backgroundColor: theme.palette.custom.row,
                }}
              >
                <Table
                  aria-labelledby="linked-simid-list-header"
                  sx={{
                    "& th, & td": {
                      textAlign: "center",
                      verticalAlign: "middle",
                    },
                    "& th": {
                      color: "#fff",
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: theme.palette.custom.tableHeader,
                      zIndex: 1,
                    }}
                  >
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                        }}
                      >
                        <Typography
                          id="linked-simid-list-header"
                          gutterBottom
                          sx={{
                            fontWeight: "bold",
                            color: "#fff",
                            marginBottom: 0,
                            backgroundColor: theme.palette.custom.tableHeader,
                            textAlign: "center",
                          }}
                        >
                          紐づけ済一覧
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow aria-labelledby="linked-simid-list-header">
                      <TableCell
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                          width: columnWidths.simId,
                        }}
                      >
                        IMSI
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                          width: columnWidths.serialNo,
                        }}
                      >
                        シリアルNo
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                          width: columnWidths.companyCode,
                        }}
                      >
                        利用企業コード
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                          width: columnWidths.companyName,
                        }}
                      >
                        利用企業名
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "2px 4px",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          verticalAlign: "middle",
                          width: columnWidths.action,
                        }}
                      >
                        アクション
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ textAlign: "center", py: 2 }}
                        >
                          検索結果はありません。
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                padding: "2px 4px",
                                verticalAlign: "middle",
                              }}
                            >
                              {editIndex === index ? (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  margin="dense"
                                  value={editFormData.simId ?? ""}
                                  inputProps={{ maxLength: 15 }}
                                  name="simId"
                                  onChange={handleChange}
                                  sx={{
                                    "& .MuiInputBase-root": { padding: "2px" },
                                    "& .MuiOutlinedInput-input": {
                                      padding: "2px",
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  {row.simId}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px 4px",
                                verticalAlign: "middle",
                              }}
                            >
                              {editIndex === index ? (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  margin="dense"
                                  value={editFormData.serialNo ?? ""}
                                  name="serialNo"
                                  onChange={handleChange}
                                  inputProps={{ maxLength: 6 }}
                                  sx={{
                                    "& .MuiInputBase-root": { padding: "2px" },
                                    "& .MuiOutlinedInput-input": {
                                      padding: "2px",
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  {row.serialNo}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px 4px",
                                verticalAlign: "middle",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {row.companyCode}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                padding: "2px 4px",
                                verticalAlign: "middle",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {row.companyName}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: "2px 4px",
                                verticalAlign: "middle",
                              }}
                            >
                              {editIndex === index ? (
                                <>
                                  <Tooltip title={"保存"} placement="top" arrow>
                                    <IconButton onClick={handleUpdate}>
                                      <SaveIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={"キャンセル"}
                                    placement="top"
                                    arrow
                                  >
                                    <IconButton onClick={handleCancel}>
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title={"編集"} placement="top" arrow>
                                    <span>
                                      <IconButton
                                        onClick={() => handleEdit(index)}
                                        disabled={
                                          !isNullOrEmpty(row.companyCode) &&
                                          !isNullOrEmpty(row.companyName)
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={"削除"} placement="top" arrow>
                                    <span>
                                      <IconButton
                                        onClick={() =>
                                          handleConfirmModalOpen(
                                            "選択したデータを削除します。よろしいですか？",
                                            () => handleDelete(index)
                                          )
                                        }
                                        disabled={
                                          !isNullOrEmpty(row.companyCode) &&
                                          !isNullOrEmpty(row.companyName)
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default SimSerialNoMapping;
